import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { getImageUrl } from '../utils/imageUrl';
import { toast } from 'react-toastify';
import { createPedido, getPrazoConfig } from '../services/api';
import './Checkout.css';

const FORMAS_PAGAMENTO = [
  { value: 'pix', label: 'Pix' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'debito', label: 'Débito' },
  { value: 'credito', label: 'Crédito' },
  { value: 'prazo', label: 'Prazo' },
];

const Checkout = () => {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [prazoConfig, setPrazoConfig] = useState({ maxParcelas: 3, juros: {} });
  const [formaPagamento, setFormaPagamento] = useState('pix');
  const [parcelasPrazo, setParcelasPrazo] = useState(1);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    getPrazoConfig()
      .then((res) => {
        const d = res.data || {};
        setPrazoConfig({
          maxParcelas: Math.min(24, Math.max(1, parseInt(d.maxParcelas, 10) || 3)),
          juros: d.juros && typeof d.juros === 'object' ? d.juros : {}
        });
      })
      .catch(() => setPrazoConfig({ maxParcelas: 3, juros: {} }));
  }, []);

  const jurosParaParcelas = formaPagamento === 'prazo' ? (parseFloat(prazoConfig.juros[String(parcelasPrazo)]) || 0) : 0;
  const totalComJuros = formaPagamento === 'prazo'
    ? Math.round(totalPrice * (1 + jurosParaParcelas / 100) * 100) / 100
    : totalPrice;
  const valorParcela = formaPagamento === 'prazo' && parcelasPrazo >= 1
    ? Math.round((totalComJuros / parcelasPrazo) * 100) / 100
    : null;

  if (items.length === 0 && !loading) {
    return (
      <div className="checkout-page">
        <div className="container">
          <h1 className="page-title">Finalizar compra</h1>
          <div className="checkout-vazio">
            <p>Seu carrinho está vazio. Adicione produtos para finalizar a compra.</p>
            <Link to="/produtos" className="btn-secondary">Ir para produtos</Link>
          </div>
        </div>
      </div>
    );
  }

  const handleFinalizar = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) {
      toast.info('Faça login para finalizar a compra.');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        itens: items.map(({ produto, quantidade, corSelecionada }) => ({
          produto_id: produto.id,
          quantidade,
          preco_unitario: parseFloat(produto.preco),
          nome_produto: produto.nome,
          cor_selecionada: corSelecionada?.descricao_cor || null,
          produto,
        })),
        total: totalPrice,
        forma_pagamento: formaPagamento,
      };
      if (formaPagamento === 'prazo') {
        payload.parcelas = parcelasPrazo;
      }
      await createPedido(payload);
      if (formaPagamento === 'prazo') {
        toast.success('Pedido enviado! Aguarde a aprovação do administrador para pagamento em prazo.');
      } else {
        toast.success('Pedido registrado! Acompanhe em Minha Conta.');
      }
      clearCart();
      navigate('/minha-conta', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao registrar pedido.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="page-title">Finalizar compra</h1>
        <form onSubmit={handleFinalizar} className="checkout-layout">
          <div className="checkout-form">
            <section className="checkout-section">
              <h2>Produtos selecionados</h2>
              <ul className="checkout-itens">
                {items.map(({ produto, quantidade, corSelecionada }) => {
                  const foto = corSelecionada?.foto ? getImageUrl(corSelecionada.foto) : getImageUrl((produto.fotos && produto.fotos[0]) || produto.foto);
                  const itemKey = `${produto.id}-${(corSelecionada?.descricao_cor || '').trim()}`;
                  return (
                    <li key={itemKey} className="checkout-item">
                      {foto ? (
                        <img src={foto} alt={produto.nome} loading="lazy" />
                      ) : (
                        <div className="no-image">Sem imagem</div>
                      )}
                      <div>
                        <strong>{produto.nome}</strong>
                        {corSelecionada?.descricao_cor && (
                          <span className="checkout-item-cor">Opcao: {corSelecionada.descricao_cor}</span>
                        )}
                        <span>Qtd: {quantidade} × R$ {parseFloat(produto.preco).toFixed(2)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="checkout-section checkout-pagamento">
              <h2>Forma de pagamento</h2>
              <div className="checkout-formas-pagamento">
                {FORMAS_PAGAMENTO.map((fp) => (
                  <label key={fp.value} className="checkout-forma-option">
                    <input
                      type="radio"
                      name="forma_pagamento"
                      value={fp.value}
                      checked={formaPagamento === fp.value}
                      onChange={() => setFormaPagamento(fp.value)}
                    />
                    <span>{fp.label}</span>
                  </label>
                ))}
              </div>

              {formaPagamento === 'prazo' && (
                <div className="checkout-prazo-opcoes">
                  <p className="checkout-prazo-info">
                    Pagamento em até {prazoConfig.maxParcelas}x. Aguardará aprovação do administrador.
                    {jurosParaParcelas > 0 && ` Juros para ${parcelasPrazo}x: ${jurosParaParcelas}%.`}
                  </p>
                  <div className="checkout-parcelas">
                    {Array.from({ length: prazoConfig.maxParcelas }, (_, i) => i + 1).map((n) => {
                      const jurosN = parseFloat(prazoConfig.juros[String(n)]) || 0;
                      const totalN = Math.round(totalPrice * (1 + jurosN / 100) * 100) / 100;
                      const parcelaN = Math.round((totalN / n) * 100) / 100;
                      return (
                        <label key={n} className="checkout-parcela-option">
                          <input
                            type="radio"
                            name="parcelas"
                            value={n}
                            checked={parcelasPrazo === n}
                            onChange={() => setParcelasPrazo(n)}
                          />
                          <span>
                            {n === 1 ? `Total (1x)` : `Em ${n}x`}
                            {jurosN > 0 && ` — ${jurosN}% juros`}
                            {' — '}R$ {parcelaN.toFixed(2)}/parcela
                            {n > 1 && ` (total R$ ${totalN.toFixed(2)})`}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          </div>

          <aside className="checkout-sidebar">
            <div className="checkout-total">
              <strong>Total: R$ {formaPagamento === 'prazo' ? totalComJuros.toFixed(2) : totalPrice.toFixed(2)}</strong>
              {formaPagamento === 'prazo' && parcelasPrazo > 1 && valorParcela != null && (
                <p className="checkout-parcela-resumo">
                  {parcelasPrazo}x de R$ {valorParcela.toFixed(2)}
                </p>
              )}
            </div>
            <button type="submit" className="btn-finalizar" disabled={loading}>
              {loading ? 'Processando...' : 'Confirmar pedido'}
            </button>
            <Link to="/carrinho" className="btn-voltar">Voltar ao carrinho</Link>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
